// src/services/signup/pendingOrgsService.js
import { auth, db } from '../../firebaseConfig';
import { doc, getDoc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';

const COL = 'pendingOrgs';

export function pendingOrgRef(uid) {
  return doc(db, COL, uid);
}

/**
 * Create or update the caller's own pending org doc.
 * - Ensures we are signed in
 * - Ensures uid === auth.currentUser.uid
 * - Forces a token fetch to avoid rare "unauthenticated" races just after sign-up
 * - Logs & rethrows the real Firebase error so you don't just see "WebChannel terminate 400"
 */
export async function createOrUpdatePendingOrg(uid, data) {
  const user = auth.currentUser;
  if (!user) {
    const err = new Error('Unauthenticated: no current user');
    err.code = 'auth/not-signed-in';
    throw err;
  }
  if (user.uid !== uid) {
    const err = new Error(`UID mismatch: auth.uid=${user.uid} payload.uid=${uid}`);
    err.code = 'auth/uid-mismatch';
    throw err;
  }

  // Ensure an ID token exists (avoids race immediately after account creation)
  try {
    await user.getIdToken(/* forceRefresh */ false);
  } catch (_) {
    await user.getIdToken(true);
  }

  const ref = pendingOrgRef(uid);

  try {
    await setDoc(
      ref,
      {
        ...data,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  } catch (e) {
    const code = e?.code || 'unknown';
    const msg = e?.message || 'Unknown Firestore error during pendingOrgs write';
    // eslint-disable-next-line no-console
    console.error('[pendingOrgs] setDoc failed:', code, msg, e);
    throw e;
  }
}

export async function readPendingOrg(uid = auth.currentUser?.uid) {
  const user = auth.currentUser;
  if (!uid || !user) {
    const err = new Error('Unauthenticated read of pendingOrgs');
    err.code = 'auth/not-signed-in';
    throw err;
  }
  if (user.uid !== uid) {
    const err = new Error(`UID mismatch on read: auth.uid=${user.uid} requested.uid=${uid}`);
    err.code = 'auth/uid-mismatch';
    throw err;
  }
  try {
    return await getDoc(pendingOrgRef(uid));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[pendingOrgs] getDoc failed:', e?.code, e?.message, e);
    throw e;
  }
}

export async function deletePendingOrg(uid = auth.currentUser?.uid) {
  const user = auth.currentUser;
  if (!uid || !user) {
    const err = new Error('Unauthenticated delete of pendingOrgs');
    err.code = 'auth/not-signed-in';
    throw err;
  }
  if (user.uid !== uid) {
    const err = new Error(`UID mismatch on delete: auth.uid=${user.uid} requested.uid=${uid}`);
    err.code = 'auth/uid-mismatch';
    throw err;
  }
  try {
    await deleteDoc(pendingOrgRef(uid));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[pendingOrgs] deleteDoc failed:', e?.code, e?.message, e);
    // best-effort cleanup; do not rethrow
  }
}
